// The floating "File a bug" button, bottom-left on every page.
//
// Press it and the page is photographed FIRST (so the dialog is not in the
// picture), then the dialog asks for the one thing a machine cannot capture:
// what the person was doing and what went wrong. Everything else is gathered
// by services/bugReportService.js from what the browser already knows.
// One Send files a DevOps Bug and emails Patrick (analytics
// POST /api/bug-reports).

import { useCallback, useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Checkbox, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, Fab, FormControlLabel, Link, TextField, Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { captureContext, captureScreenshot, fileBugReport } from '../../services/bugReportService';
import { Explain } from '../Work/explain';
import X from '../Work/explainText';

const TIPS = [
    ['What you were trying to do', '“I was assigning Riley as owner on a client’s to-do…”'],
    ['What actually happened', '“…I hit Save and the popup just sat there, spinning.”'],
    ['What you expected instead', '“I expected Riley’s name to show up on the row.”'],
];

const fmtDue = (iso) => {
    const d = iso ? new Date(`${iso.slice(0, 10)}T12:00:00`) : null;
    return d && !Number.isNaN(d.getTime())
        ? d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
        : null;
};

// What the server actually set on the Bug (bug_report_router.create_bug), so
// nobody has to open DevOps to learn where it went or when it is due.
export function FiledDefaults({ ticket }) {
    const due = fmtDue(ticket.due_on);
    const others = (ticket.stakeholders || []).length - 1;
    return (
        <Typography sx={{ fontSize: 13, opacity: 0.85, mb: 1.5 }} data-testid="bug-filed-defaults">
            It’s on the Super Portal board’s Inbox
            {ticket.owner ? <>, owned by {ticket.owner}</> : null}
            {due ? <>, due {due}</> : null}.
            {(ticket.stakeholders || []).length
                ? <> You’re listed as a stakeholder{others > 0 ? <>, with {others} reviewer{others === 1 ? '' : 's'}</> : null}.</>
                : null}
        </Typography>
    );
}

// The roadmap Epic the server filed the Bug under, or that it went in loose
// (#4213). No stage in the reply (a server before #4213) says nothing rather
// than claiming "loose".
export function FiledStage({ stage }) {
    if (!stage) return null;
    return (
        <Explain text={X.bugStage(stage)}>
            <Typography sx={{ fontSize: 13, opacity: 0.85, mb: 1.5 }} data-testid="bug-filed-stage">
                {stage.epic_id
                    ? <>On the roadmap under {stage.epic_title || `#${stage.epic_id}`}.</>
                    : <>Not on a roadmap stage yet: it’s tagged loose for the weekly roadmap tidy-up.</>}
            </Typography>
        </Explain>
    );
}

const summarise = (context) => Object.entries(context || {}).map(([name, section]) => {
    if (Array.isArray(section)) return `${name}: ${section.length} entr${section.length === 1 ? 'y' : 'ies'}`;
    if (section && typeof section === 'object') {
        const filled = Object.values(section).filter((v) => v !== '' && v != null && !(Array.isArray(v) && !v.length));
        return `${name}: ${filled.length} detail${filled.length === 1 ? '' : 's'}`;
    }
    return name;
});

export default function BugReportButton() {
    const [open, setOpen] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const [description, setDescription] = useState('');
    const [expected, setExpected] = useState('');
    const [context, setContext] = useState(null);
    const [screenshot, setScreenshot] = useState(null);
    const [includeShot, setIncludeShot] = useState(true);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null);

    const start = useCallback(async () => {
        setCapturing(true);
        setError('');
        setResult(null);
        // Photograph the page as it is, before the dialog covers it.
        const [ctx, shot] = await Promise.all([captureContext(), captureScreenshot()]);
        setContext(ctx);
        setScreenshot(shot);
        setIncludeShot(!!shot);
        setCapturing(false);
        setOpen(true);
    }, []);

    const close = () => {
        setOpen(false);
        if (result) { setDescription(''); setExpected(''); setResult(null); }
    };

    // Paste your own screenshot to swap it in.
    useEffect(() => {
        if (!open) return undefined;
        const onPaste = (e) => {
            const item = Array.from(e.clipboardData?.items || []).find((i) => i.type.startsWith('image/'));
            if (!item) return;
            const reader = new FileReader();
            reader.onload = () => { setScreenshot(reader.result); setIncludeShot(true); };
            reader.readAsDataURL(item.getAsFile());
        };
        window.addEventListener('paste', onPaste);
        return () => window.removeEventListener('paste', onPaste);
    }, [open]);

    const send = async () => {
        setSending(true);
        setError('');
        try {
            const out = await fileBugReport({
                description, expected, context, screenshot: includeShot ? screenshot : null,
            });
            setResult(out);
        } catch (e) {
            setError(e.message);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Fab
                data-bug-report-exclude=""
                variant="extended" size="small" onClick={start} disabled={capturing}
                aria-label="File a bug"
                sx={{
                    position: 'fixed', left: 20, bottom: 20, zIndex: 1290, textTransform: 'none',
                    fontWeight: 600, fontSize: 13, bgcolor: '#0b1a3a', color: '#fff', border: '1px solid #3987e5',
                    boxShadow: '0 4px 14px rgba(0,0,0,.35)', '&:hover': { bgcolor: '#13285a' },
                }}
            >
                {capturing
                    ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                    : <BugReportIcon sx={{ fontSize: 18, mr: 0.75 }} />}
                {capturing ? 'Snapping the page…' : 'File a bug'}
            </Fab>

            <Dialog open={open} onClose={sending ? undefined : close} maxWidth="sm" fullWidth
                data-bug-report-exclude="">
                <DialogTitle sx={{ pb: 0.5 }}>
                    {result ? 'Squashing has begun 🎉' : 'Spotted a bug? 🐞'}
                </DialogTitle>
                <DialogContent>
                    {result ? (
                        <Box>
                            <Typography sx={{ mb: 1.5 }}>
                                Thank you! {result.ticket
                                    ? <>Bug <Link href={result.ticket.url} target="_blank" rel="noreferrer">#{result.ticket.id}</Link> is filed</>
                                    : 'Your report is on its way'}
                                {result.emailed ? ' and Patrick has it in his inbox.' : '.'} Every report makes
                                Nexus a little better for everyone, so this one counts.
                            </Typography>
                            {result.ticket && <FiledDefaults ticket={result.ticket} />}
                            {result.ticket && <FiledStage stage={result.ticket.stage} />}
                            {(result.warnings || []).map((w) => <Alert key={w} severity="warning" sx={{ mb: 1 }}>{w}</Alert>)}
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: 14, mb: 1.5 }}>
                                Thanks for taking a second to tell us. Seriously, this is how Nexus gets better.
                                The most helpful reports answer three little questions:
                            </Typography>
                            <Box component="ol" sx={{ pl: 2.5, mt: 0, mb: 1.5, fontSize: 13.5 }}>
                                {TIPS.map(([q, eg]) => (
                                    <li key={q} style={{ marginBottom: 4 }}>
                                        <b>{q}</b> <span style={{ opacity: 0.75 }}>{eg}</span>
                                    </li>
                                ))}
                            </Box>
                            <Typography sx={{ fontSize: 12.5, opacity: 0.8, mb: 2 }}>
                                No need to be technical. “It happens every time” or “it only happened once” are
                                both gold. We’ll quietly attach the boring bits for you: the page you’re on, what
                                you’ve clicked through recently, your browser and a screenshot. It all stays inside
                                Nexus, in DevOps and Patrick’s inbox.
                            </Typography>

                            <TextField
                                label="What happened?" required multiline minRows={4} fullWidth autoFocus
                                placeholder="I was trying to… I clicked… and then…"
                                value={description} onChange={(e) => setDescription(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="What did you expect to happen? (optional)" multiline minRows={2} fullWidth
                                value={expected} onChange={(e) => setExpected(e.target.value)} sx={{ mb: 1.5 }}
                            />

                            {screenshot ? (
                                <Box sx={{ mb: 1 }}>
                                    <FormControlLabel
                                        control={<Checkbox size="small" checked={includeShot}
                                            onChange={(e) => setIncludeShot(e.target.checked)} />}
                                        label={<Typography sx={{ fontSize: 13 }}>Include this screenshot</Typography>}
                                    />
                                    {includeShot && (
                                        <Box component="img" src={screenshot} alt="Screenshot of the page"
                                            sx={{ display: 'block', maxWidth: '100%', maxHeight: 180, borderRadius: 1,
                                                border: '1px solid rgba(127,127,127,.4)' }} />
                                    )}
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 12.5, opacity: 0.8, mb: 1 }}>
                                    We couldn’t snap this page automatically, sorry about that.
                                </Typography>
                            )}
                            <Typography sx={{ fontSize: 12, opacity: 0.7, mb: 1.5 }}>
                                Got a better screenshot? Paste it (⌘V / Ctrl+V) anywhere in this box to swap it in.
                            </Typography>

                            <Accordion disableGutters elevation={0}
                                sx={{ bgcolor: 'transparent', border: '1px solid rgba(127,127,127,.3)', '&:before': { display: 'none' } }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography sx={{ fontSize: 13 }}>Peek at what we’ll send along</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ pt: 0 }}>
                                    <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 12.5 }}>
                                        {summarise(context).map((s) => <li key={s}>{s}</li>)}
                                        <li>Your recent clicks through Nexus (from the activity log we already keep)</li>
                                    </Box>
                                    <Box component="pre" sx={{ fontSize: 11, maxHeight: 220, overflow: 'auto', mt: 1,
                                        p: 1, borderRadius: 1, bgcolor: 'rgba(127,127,127,.12)', whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(context, null, 2)}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>

                            {error && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                    Hmm, that didn’t go through: {error}. Your words are still here, so give it
                                    another go in a moment.
                                </Alert>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {result ? (
                        <Button onClick={close} variant="contained">Back to work</Button>
                    ) : (
                        <>
                            <Button onClick={close} disabled={sending}>Never mind</Button>
                            <Button onClick={send} variant="contained" disabled={sending || !description.trim()}
                                startIcon={sending ? <CircularProgress size={14} /> : null}>
                                {sending ? 'Sending…' : 'Send it 🚀'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
